"use client"

import { ArrowRight, Check, Loader2 } from "lucide-react"

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet"
import { useIsMobile } from "@/hooks/use-mobile"
import { cn } from "@/lib/utils"
import type { StageOption } from "@/hooks/use-candidate-stage"

/**
 * Moving a candidate's stage, from canvas artboards 14d (wide) and 14g
 * (narrow), with 14k/14n as the light pair.
 *
 * "The eight stages read as one ladder with hairlines between them, current
 * dimmed, target lit by a teal rule — the same object as the mobile sheet."
 * Desktop is a centred modal, narrow is a bottom sheet: "one decision, one
 * screen."
 *
 * Presentational — the ladder and the move all arrive as props from
 * `useCandidateStage`.
 */

/** One rung. Identical on both breakpoints bar its height and corners. */
function StageRow({
  stage,
  onSelect,
  compact,
}: {
  stage: StageOption
  onSelect: () => void
  compact?: boolean
}) {
  const isTarget = stage.relation === "target"
  const isCurrent = stage.relation === "current"
  const isRejected = stage.id === "rejected"

  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={isCurrent}
      aria-pressed={isTarget}
      className={cn(
        "flex w-full items-center gap-3 border-t text-left transition-colors",
        compact ? "h-11 rounded-lg border-t-0 px-2.5" : "h-[42px] px-3.5",
        isCurrent && "cursor-default opacity-50",
        !isCurrent && !isTarget && "hover:bg-accent",
        isTarget && !compact && "bg-primary/[0.04] pl-[13px] shadow-[inset_2px_0_0_var(--primary)]",
        isTarget && compact && "bg-primary/[0.08]",
      )}
    >
      <span
        className="h-1.5 w-1.5 flex-none rounded-full"
        style={{ backgroundColor: stage.hex }}
      />
      <span
        className={cn(
          "flex-1 text-[13px]",
          isTarget ? "font-medium text-foreground" : "text-muted-foreground",
          isRejected && !isTarget && "text-destructive",
        )}
      >
        {stage.id}
      </span>
      <span
        className={cn(
          "font-mono text-[10.5px]",
          isTarget ? "text-brand-ink" : "text-muted-foreground",
        )}
      >
        {stage.relation}
      </span>
      <span className="flex w-3.5 justify-end">
        {isTarget && <Check className="h-3.5 w-3.5 text-brand-ink" />}
      </span>
    </button>
  )
}

function Ladder({
  stages,
  onSelect,
  compact,
}: {
  stages: StageOption[]
  onSelect: (id: string) => void
  compact?: boolean
}) {
  return (
    <div className={compact ? "px-2.5 py-1.5" : "px-3 pt-3.5"}>
      {stages.map((stage) => (
        <div
          key={stage.id}
          // Rejected is not the next rung of the ladder, so it sits apart.
          className={stage.id === "rejected" ? "mt-1.5" : undefined}
        >
          <StageRow
            stage={stage}
            compact={compact}
            onSelect={() => onSelect(stage.id)}
          />
        </div>
      ))}
    </div>
  )
}

function Body({
  subtitle,
  stages,
  target,
  onSelect,
  onMove,
  onCancel,
  isMoving,
  canMove,
  compact,
}: {
  subtitle: string
  stages: StageOption[]
  target?: string
  onSelect: (id: string) => void
  onMove: () => void
  onCancel: () => void
  isMoving: boolean
  canMove: boolean
  compact?: boolean
}) {
  return (
    <>
      <div
        className={cn(
          "flex-none border-b",
          compact ? "px-4 pb-3.5 pt-2" : "px-[26px] pb-[18px] pt-5",
        )}
      >
        <DialogOrSheetTitle compact={compact} />
        <p className="mt-1.5 text-[12px] leading-[1.5] text-muted-foreground">
          {subtitle}
        </p>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <Ladder stages={stages} onSelect={onSelect} compact={compact} />
      </div>

      {!canMove && (
        <p
          className={cn(
            "flex-none text-[11.5px] text-muted-foreground",
            compact ? "px-4 pb-1" : "px-[26px] pb-1",
          )}
        >
          This candidate is not on a job&rsquo;s pipeline, so there is no stage to
          move along.
        </p>
      )}

      {/* Narrow gets a real button; wide keeps the record's quiet text actions. */}
      {compact ? (
        <div className="flex flex-none flex-col gap-2.5 px-4 pb-5 pt-3.5">
          <button
            type="button"
            onClick={onMove}
            disabled={!target || !canMove || isMoving}
            className="flex h-12 items-center justify-center gap-2 rounded-[10px] bg-primary text-[14px] font-semibold text-primary-foreground disabled:opacity-40"
          >
            {isMoving && <Loader2 className="h-4 w-4 animate-spin" />}
            {target ? `Move to ${target}` : "Pick a stage"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="flex h-11 items-center justify-center text-[13px] text-muted-foreground"
          >
            Cancel
          </button>
        </div>
      ) : (
        <div className="flex flex-none items-center gap-[22px] border-t px-[26px] pb-5 pt-[18px]">
          <button
            type="button"
            onClick={onMove}
            disabled={!target || !canMove || isMoving}
            className="inline-flex items-center gap-2 text-[12.5px] font-medium text-brand-ink transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {isMoving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <ArrowRight className="h-3.5 w-3.5" />
            )}
            {target ? `Move to ${target}` : "Pick a stage"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="text-[12.5px] text-muted-foreground transition-colors hover:text-foreground"
          >
            Cancel
          </button>
        </div>
      )}
    </>
  )
}

/** The title element differs by container, but the type does not. */
function DialogOrSheetTitle({ compact }: { compact?: boolean }) {
  const className = "text-[16px] font-semibold leading-[1.2] tracking-[-0.02em]"
  return compact ? (
    <SheetTitle className={className}>Move stage</SheetTitle>
  ) : (
    <DialogTitle className={className}>Move stage</DialogTitle>
  )
}

export function MoveStageDialog({
  open,
  onOpenChange,
  subtitle,
  stages,
  target,
  onSelect,
  onMove,
  isMoving,
  canMove,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** "Maya Aslan · Senior Platform Engineer @ Northwind" */
  subtitle: string
  stages: StageOption[]
  target?: string
  onSelect: (id: string) => void
  onMove: () => void
  isMoving: boolean
  canMove: boolean
}) {
  const isMobile = useIsMobile()
  const close = () => onOpenChange(false)

  const body = (
    <Body
      subtitle={subtitle}
      stages={stages}
      target={target}
      onSelect={onSelect}
      onMove={onMove}
      onCancel={close}
      isMoving={isMoving}
      canMove={canMove}
      compact={isMobile}
    />
  )

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="bottom"
          className="flex max-h-[86vh] flex-col gap-0 rounded-t-[18px] p-0"
        >
          {body}
        </SheetContent>
      </Sheet>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        /*
          Radix focuses the first focusable child on open, which here is the
          first rung — and a focused rung is indistinguishable from a chosen
          one. Focus the panel instead: the ladder still tabs, Esc still
          closes, and nothing looks picked until something is.
        */
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          if (event.currentTarget instanceof HTMLElement) {
            event.currentTarget.focus()
          }
        }}
        tabIndex={-1}
        className="flex max-h-[86vh] w-[560px] max-w-[calc(100vw-2rem)] flex-col gap-0 p-0 focus:outline-none"
      >
        {body}
      </DialogContent>
    </Dialog>
  )
}
